/**
 * Every server interaction the application makes, as TanStack Query hooks.
 *
 * Public reads are cached and shared between pages; company writes invalidate
 * the dashboard and the boards a listing appears on, so a raise or a renewal is
 * reflected without a manual refresh.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  serializeScope,
  type ApplyClickResponse,
  type CheckoutResponse,
  type CompanyProfileInput,
  type CompanyProfileResponse,
  type CompanyPublicResponse,
  type ConfirmReservationResponse,
  type FeedbackRequest,
  type JobDetail,
  type JobInput,
  type JobResponse,
  type LeaderboardResponse,
  type LeaderboardScope,
  type LogoUploadUrlResponse,
  type MyJobsResponse,
  type ParseJdRequest,
  type ParseJdResponse,
  type RenewResponse,
  type ReserveListingResponse,
} from '@bidtohire/shared';

import { apiRequest, ApiRequestError, type RequestOptions } from './api';
import { useAuth } from './auth-context';

export const queryKeys = {
  config: ['config'] as const,
  leaderboard: (scope: string) => ['leaderboard', scope] as const,
  job: (id: string) => ['job', id] as const,
  company: (slug: string) => ['company', slug] as const,
  myCompany: ['me', 'company'] as const,
  myJobs: ['me', 'jobs'] as const,
};

/** A request signed with the company's id token, refreshed when required. */
function useAuthedRequest(): <T>(path: string, options?: RequestOptions) => Promise<T> {
  const auth = useAuth();
  return useCallback(
    async <T,>(path: string, options: RequestOptions = {}): Promise<T> => {
      const token = await auth.token();
      if (token === undefined) {
        throw new ApiRequestError(401, 'SIGNED_OUT', 'Sign in to continue.');
      }
      return apiRequest<T>(path, { ...options, token });
    },
    [auth],
  );
}

// ---------------------------------------------------------------------------
// Public reads
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;

export function useLeaderboard(
  scope: LeaderboardScope,
  options?: { readonly enabled?: boolean },
): UseInfiniteQueryResult<{ pages: LeaderboardResponse[]; pageParams: unknown[] }, Error> {
  const serialized = serializeScope(scope);
  return useInfiniteQuery({
    queryKey: queryKeys.leaderboard(serialized),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiRequest<LeaderboardResponse>('/jobs', {
        query: { scope: serialized, limit: PAGE_SIZE, cursor: pageParam },
        signal,
      }),
    getNextPageParam: (lastPage) => lastPage.cursor,
    staleTime: 15_000,
    // The posting page asks for a board only once a specialism has been
    // picked; before that there is no scope worth reading.
    enabled: options?.enabled ?? true,
  });
}

export function useJob(id: string | undefined): UseQueryResult<JobDetail, Error> {
  return useQuery({
    queryKey: queryKeys.job(id ?? ''),
    queryFn: ({ signal }) => apiRequest<JobDetail>(`/jobs/${encodeURIComponent(id as string)}`, { signal }),
    enabled: id !== undefined && id !== '',
    staleTime: 15_000,
  });
}

export function useCompany(slug: string | undefined): UseQueryResult<CompanyPublicResponse, Error> {
  return useQuery({
    queryKey: queryKeys.company(slug ?? ''),
    queryFn: ({ signal }) =>
      apiRequest<CompanyPublicResponse>(`/companies/${encodeURIComponent(slug as string)}`, { signal }),
    enabled: slug !== undefined && slug !== '',
    staleTime: 60_000,
  });
}

/**
 * Records the outbound click and hands back the company's own posting. The
 * navigation happens from the page the seeker is already on, so a link preview
 * can never inflate the count.
 */
export function useApplyClick(): UseMutationResult<ApplyClickResponse, Error, string> {
  return useMutation({
    mutationFn: (jobId: string) =>
      apiRequest<ApplyClickResponse>(`/jobs/${encodeURIComponent(jobId)}/click`, {
        method: 'POST',
      }),
  });
}

/**
 * A bug report or a message from the footer form.
 *
 * Unauthenticated by design: a visitor who hit a broken page is exactly the
 * person least able to sign in, and requiring an account is how a product
 * stops hearing about its own bugs.
 */
export function useSubmitFeedback(): UseMutationResult<void, Error, FeedbackRequest> {
  return useMutation({
    mutationFn: (input: FeedbackRequest) =>
      apiRequest<void>('/feedback', { method: 'POST', body: input }),
  });
}

// ---------------------------------------------------------------------------
// The signed-in company
// ---------------------------------------------------------------------------

/** The company profile, or null when the account has not created one yet. */
export function useMyCompany(): UseQueryResult<CompanyProfileResponse | null, Error> {
  const request = useAuthedRequest();
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.myCompany,
    enabled: auth.isSignedIn && auth.isReady,
    queryFn: async () => {
      try {
        return await request<CompanyProfileResponse>('/companies/me');
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 404) return null;
        throw error;
      }
    },
    staleTime: 30_000,
  });
}

/**
 * Every listing this account holds, shells included.
 *
 * The return from Stripe polls this while a reservation is still waiting for
 * its webhook, which is the only reason a poll interval exists here; nothing
 * else on the dashboard asks for one.
 */
export function useMyJobs(options?: {
  readonly refetchInterval?: number | false;
}): UseQueryResult<MyJobsResponse, Error> {
  const request = useAuthedRequest();
  const auth = useAuth();
  const interval = options?.refetchInterval ?? false;
  return useQuery({
    queryKey: queryKeys.myJobs,
    enabled: auth.isSignedIn && auth.isReady,
    queryFn: () => request<MyJobsResponse>('/me/jobs'),
    staleTime: interval === false ? 10_000 : 0,
    refetchInterval: interval,
  });
}

export function useSaveCompany(): UseMutationResult<CompanyProfileResponse, Error, CompanyProfileInput> {
  const request = useAuthedRequest();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyProfileInput) =>
      request<CompanyProfileResponse>('/companies/me', { method: 'POST', body: input }),
    onSuccess: (result) => {
      client.setQueryData(queryKeys.myCompany, result);
      void client.invalidateQueries({ queryKey: queryKeys.myJobs });
    },
  });
}

/**
 * Closes the account for good: the listings, the profile, and the sign-in.
 *
 * Everything cached is dropped rather than invalidated -- there is no longer
 * an account to refetch any of it for, and a stale profile left in the cache
 * would render for a moment on the way out.
 */
/**
 * Closing an account answers 204 when everything went, or a body saying the
 * sign-in outlived the data. That one case has to reach the dialog: the promise
 * made on the way in is that the email address becomes free to register again.
 */
export function useDeleteCompany(): UseMutationResult<
  { readonly signInRemoved: boolean } | undefined,
  Error,
  void
> {
  const request = useAuthedRequest();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      request<{ readonly signInRemoved: boolean } | undefined>('/companies/me', {
        method: 'DELETE',
      }),
    onSuccess: () => {
      client.clear();
    },
  });
}

/**
 * Reserves a listing: the first step of posting, and the only one that costs
 * anything. The server mints the shell and answers with the Checkout page to
 * hand the browser to; the amount is re-derived there, never trusted from here.
 */
export function useReserveListing(): UseMutationResult<ReserveListingResponse, Error, number> {
  const request = useAuthedRequest();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (amountCents: number) =>
      request<ReserveListingResponse>('/me/checkout', {
        method: 'POST',
        body: { amountCents },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.myJobs });
    },
  });
}

/**
 * Settles a session whose webhook has not arrived. Answering `paid: false` is
 * ordinary — it means the payment is still in flight, and the caller keeps
 * waiting rather than treating it as a failure.
 */
export function useConfirmReservation(): UseMutationResult<
  ConfirmReservationResponse,
  Error,
  string
> {
  const request = useAuthedRequest();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      request<ConfirmReservationResponse>('/me/checkout/confirm', {
        method: 'POST',
        body: { sessionId },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.myJobs });
    },
  });
}

/**
 * The posting details, submitted against a reservation. On a reservation that
 * is already paid for — the ordinary case — this is the write that publishes.
 */
export function useSubmitContent(): UseMutationResult<
  JobResponse,
  Error,
  { readonly id: string; readonly input: JobInput }
> {
  const request = useAuthedRequest();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: JobInput }) =>
      request<JobResponse>(`/me/jobs/${encodeURIComponent(id)}/content`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (_result, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.myJobs });
      void client.invalidateQueries({ queryKey: queryKeys.job(variables.id) });
      void client.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}

/**
 * Reads a posting off its own page.
 *
 * An `ok: false` answer arrives under a 200 and is not an error: some sites
 * refuse automated readers, and the form simply appears empty with a line
 * saying so. Only a bad address, an account with nothing paid for, and an
 * exhausted allowance throw.
 */
export function useParseJd(): UseMutationResult<ParseJdResponse, Error, ParseJdRequest> {
  const request = useAuthedRequest();
  return useMutation({
    mutationFn: (input: ParseJdRequest) =>
      request<ParseJdResponse>('/me/parse-jd', { method: 'POST', body: input }),
  });
}

export function useUpdateJob(): UseMutationResult<
  JobResponse,
  Error,
  { readonly id: string; readonly input: JobInput }
> {
  const request = useAuthedRequest();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: JobInput }) =>
      request<JobResponse>(`/me/jobs/${encodeURIComponent(id)}`, { method: 'PUT', body: input }),
    onSuccess: (_result, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.myJobs });
      void client.invalidateQueries({ queryKey: queryKeys.job(variables.id) });
    },
  });
}

/**
 * Removes a listing. A reservation that has been paid for needs the forfeit
 * acknowledged, because the money is not coming back and the server refuses to
 * act on a request that has not said so.
 */
export function useDeleteJob(): UseMutationResult<
  void,
  Error,
  { readonly id: string; readonly forfeit?: boolean }
> {
  const request = useAuthedRequest();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, forfeit }: { id: string; forfeit?: boolean }) =>
      request<void>(`/me/jobs/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        ...(forfeit === true ? { query: { forfeit: 'true' } } : {}),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.myJobs });
      void client.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}

export function useRenewJob(): UseMutationResult<RenewResponse, Error, string> {
  const request = useAuthedRequest();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request<RenewResponse>(`/me/jobs/${encodeURIComponent(id)}/renew`, { method: 'POST' }),
    onSuccess: (_result, id) => {
      void client.invalidateQueries({ queryKey: queryKeys.myJobs });
      void client.invalidateQueries({ queryKey: queryKeys.job(id) });
    },
  });
}

/**
 * Starts a Checkout session. The request names the lifetime total the listing
 * should hold; the server derives the amount actually charged, which is the
 * difference between that total and what the listing already holds.
 */
export function useCreateCheckout(): UseMutationResult<
  CheckoutResponse,
  Error,
  { readonly jobId: string; readonly totalCents: number }
> {
  const request = useAuthedRequest();
  return useMutation({
    mutationFn: ({ jobId, totalCents }: { jobId: string; totalCents: number }) =>
      request<CheckoutResponse>(`/me/jobs/${encodeURIComponent(jobId)}/checkout`, {
        method: 'POST',
        body: { totalCents },
      }),
  });
}

/** Uploads a logo straight to S3 on a presigned URL; no image passes the API. */
export function useLogoUpload(): UseMutationResult<string, Error, File> {
  const request = useAuthedRequest();
  return useMutation({
    mutationFn: async (file: File) => {
      const issued = await request<LogoUploadUrlResponse>('/me/logo-upload-url', {
        method: 'POST',
        body: { contentType: file.type, contentLength: file.size },
      });

      const response = await fetch(issued.uploadUrl, {
        method: 'PUT',
        headers: {
          'content-type': file.type,
          'cache-control': 'public, max-age=31536000, immutable',
        },
        body: file,
      });
      if (!response.ok) {
        throw new ApiRequestError(response.status, 'UPLOAD_FAILED', 'The logo could not be uploaded.');
      }
      return issued.key;
    },
  });
}
